import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { useGSAPContext } from "@/lib/gsap-context";

export function useMagneticButton(options = {}) {
  const ref = useRef(null);
  const { reducedMotion } = useGSAPContext();
  const { strength = 0.35, scale = 1.04 } = options;

  useEffect(() => {
    if (reducedMotion || !ref.current) return;

    const el = ref.current;
    let rafId = null;
    let currentX = 0;
    let currentY = 0;
    let targetX = 0;
    let targetY = 0;

    const lerp = (a, b, n) => (1 - n) * a + n * b;

    const animate = () => {
      currentX = lerp(currentX, targetX, 0.15);
      currentY = lerp(currentY, targetY, 0.15);
      gsap.set(el, { x: currentX, y: currentY });
      rafId = requestAnimationFrame(animate);
    };

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const distX = e.clientX - centerX;
      const distY = e.clientY - centerY;
      targetX = distX * strength;
      targetY = distY * strength;
    };

    const onEnter = () => {
      gsap.to(el, { scale, duration: 0.35, ease: "power2.out" });
      if (!rafId) animate();
    };

    const onLeave = () => {
      targetX = 0;
      targetY = 0;
      gsap.to(el, { scale: 1, duration: 0.4, ease: "elastic.out(1, 0.5)" });
      setTimeout(() => {
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
          currentX = 0;
          currentY = 0;
          gsap.set(el, { x: 0, y: 0 });
        }
      }, 400);
    };

    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);

    return () => {
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      if (rafId) cancelAnimationFrame(rafId);
      gsap.killTweensOf(el);
    };
  }, [reducedMotion, strength, scale]);

  return ref;
}
